import { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Section } from "@/components/section";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { RefreshButton } from "@/components/list/refresh-button";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { UserChip } from "@/components/user-chip";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { deleteFetcherKeyed, fetcher, postFetcher } from "@/lib/swr";
import type { User } from "@/lib/swr";
import type { App } from "@/types/domain";
import { BotProfileDialog } from "@/components/bots/bot-profile-dialog";

type BotRow = Pick<User, "id" | "name" | "avatarThumb" | "roles" | "createdAt"> & {
  kind?: "bot";
};
type TokenResponse = { bot: BotRow; token: string };

// App Settings → Bots: app-level bot accounts (user.kind=bot). A bot logs in
// to the wed skill with its token and sees exactly the apps it is bound to —
// the same user_visible_app rows as a person. Owner or admin; the server
// re-checks. Binding an existing bot to another app goes through Members.
export const AppBotsSection = ({ app }: { app: App }) => {
  const apiBase = `/apps/${app.id}/bots`;
  const {
    data: bots,
    isLoading,
    isValidating,
    mutate: refresh,
  } = useSWR<BotRow[]>([apiBase], fetcher);
  const list = bots ?? [];
  const [issued, setIssued] = useState<TokenResponse | null>(null);

  return (
    <Section
      title="Bots"
      action={
        <div className="flex items-center gap-2">
          <RefreshButton spinning={isValidating} onClick={() => refresh()} />
          <CreateBotDialog
            apiBase={apiBase}
            onCreated={(res) => {
              setIssued(res);
              refresh();
            }}
          />
        </div>
      }
    >
      <p className="text-xs text-muted-foreground">
        机器人账号:用 token 登录 wed skill(<code>login.js --bot</code>,token
        放 <code>WED_BOT_TOKEN</code>),可见范围 = 绑定的 App,与成员同一套权限。
        要把已有 bot 绑到本 App,在 Members 里添加即可。
      </p>
      <div className="flex flex-col gap-1">
        {isLoading && <ListLoading />}
        {!isLoading && list.length === 0 && <ListEmpty>No bots.</ListEmpty>}
        {list.map((bot, i) => (
          <BotRowCard
            key={bot.id}
            bot={bot}
            index={i}
            apiBase={apiBase}
            onChanged={() => refresh()}
            onToken={setIssued}
          />
        ))}
      </div>
      <TokenDialog issued={issued} onClose={() => setIssued(null)} />
    </Section>
  );
};

const BotRowCard = ({
  bot,
  index,
  apiBase,
  onChanged,
  onToken,
}: {
  bot: BotRow;
  index: number;
  apiBase: string;
  onChanged: () => void;
  onToken: (res: TokenResponse) => void;
}) => {
  const { toast } = useToast();
  const { trigger: rotate, isMutating: rotating } = useSWRMutation(
    `${apiBase}/${bot.id}/rotate`,
    postFetcher<TokenResponse>
  );
  const { trigger: unbind, isMutating: unbinding } = useSWRMutation(
    `${apiBase}/${bot.id}`,
    deleteFetcherKeyed
  );

  return (
    <RowCard>
      <RowIndex n={index + 1} />
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <UserChip user={{ ...bot, kind: "bot" }} />
        <span className="truncate font-mono text-xs text-muted-foreground">
          {bot.id}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <BotProfileDialog
          bot={bot}
          endpoint={`${apiBase}/${bot.id}`}
          onSaved={onChanged}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={rotating}
          onClick={async () => {
            try {
              onToken(await rotate({}));
            } catch (err) {
              toastError(toast, "Failed to rotate token", err);
            }
          }}
        >
          {rotating ? "Rotating…" : "Rotate token"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive"
          disabled={unbinding}
          onClick={async () => {
            try {
              await unbind();
              onChanged();
            } catch (err) {
              toastError(toast, "Failed to unbind bot", err);
            }
          }}
        >
          Unbind
        </Button>
      </div>
    </RowCard>
  );
};

const CreateBotDialog = ({
  apiBase,
  onCreated,
}: {
  apiBase: string;
  onCreated: (res: TokenResponse) => void;
}) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const { trigger, isMutating } = useSWRMutation(
    apiBase,
    postFetcher<TokenResponse>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">New bot</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New bot</DialogTitle>
          <DialogDescription>
            A bot account bound to this app. Its token is shown once after
            creation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="bot-name">Name</Label>
          <Input
            id="bot-name"
            value={name}
            placeholder="e.g. feishu-agent"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            disabled={isMutating || !name.trim()}
            onClick={async () => {
              try {
                const res = await trigger({ name: name.trim() });
                setOpen(false);
                setName("");
                onCreated(res);
              } catch (err) {
                toastError(toast, "Failed to create bot", err);
              }
            }}
          >
            {isMutating ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// One-time token reveal — the server only keeps a hash, so this is the only
// chance to copy it.
const TokenDialog = ({
  issued,
  onClose,
}: {
  issued: TokenResponse | null;
  onClose: () => void;
}) => {
  const { toast } = useToast();
  return (
    <Dialog open={!!issued} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bot token · {issued?.bot.name}</DialogTitle>
          <DialogDescription>
            Copy it now — it will not be shown again. Put it in the bot
            environment as WED_BOT_TOKEN.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input readOnly value={issued?.token ?? ""} className="font-mono" />
          <Button
            variant="outline"
            onClick={async () => {
              if (!issued) return;
              try {
                await navigator.clipboard.writeText(issued.token);
                toast({ title: "Copied" });
              } catch (err) {
                toastError(toast, "Copy failed", err);
              }
            }}
          >
            Copy
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
